// frontend/src/components/CsvGenerator/CsvGenerator.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './CsvGenerator.css';

interface Category {
  key: string;
  icon: string;
  label: string;
  scientific: string;
  description: string;
  examples: string;
  generator: string;
}

const CATEGORIES: Category[] = [
  {
    key: 'valor',
    icon: '📈',
    label: 'Prever um valor numérico',
    scientific: 'Regressão',
    description: 'Use quando você quer descobrir quanto algo vai valer.',
    examples: 'Exemplos: qual será o preço de venda de um imóvel? Qual o salário esperado para um cargo? Qual o custo estimado de uma obra?',
    generator: 'linear_regression',
  },
  {
    key: 'sim_nao',
    icon: '✅',
    label: 'Prever sim ou não',
    scientific: 'Classificação',
    description: 'Use quando você quer descobrir se algo vai acontecer ou não.',
    examples: 'Exemplos: este cliente vai cancelar o plano? Este pedido vai ser aprovado? Este paciente tem risco de desenvolver a doença?',
    generator: 'logistic_regression',
  },
  {
    key: 'quantidade',
    icon: '🔢',
    label: 'Prever uma quantidade',
    scientific: 'Contagem',
    description: 'Use quando você quer descobrir quantas vezes algo acontece.',
    examples: 'Exemplos: quantos sinistros um cliente vai abrir? Quantas vendas uma loja vai fazer por dia? Quantas peças vão ser produzidas por turno?',
    generator: 'poisson_regression',
  },
  {
    key: 'tempo',
    icon: '📅',
    label: 'Prever ao longo do tempo',
    scientific: 'Série Temporal',
    description: 'Use quando você quer descobrir como algo vai se comportar no futuro, mês a mês ou dia a dia.',
    examples: 'Exemplos: qual será a demanda de vendas no próximo mês? Qual o tráfego esperado do site amanhã? Qual o consumo de energia na próxima semana?',
    generator: 'time_series',
  },
];

const DEFAULT_MAX = 100000;

type Phase = 'idle' | 'generating' | 'importing' | 'done';

export const CsvGeneratorInner: React.FC = () => {
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Category | null>(null);
  const [nRegistros, setNRegistros] = useState(500);
  const [maxRegistros, setMaxRegistros] = useState(DEFAULT_MAX);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/csv/generate/limit')
      .then(r => {
        const max = r.data?.max;
        if (typeof max === 'number' && max > 0) {
          setMaxRegistros(max);
        }
      })
      .catch(() => {
        setMaxRegistros(DEFAULT_MAX);
      });
  }, []);

  const isValid = (): boolean => {
    if (!selected) {
      setError('Escolha um tipo de problema');
      return false;
    }
    if (nRegistros < 1 || nRegistros > maxRegistros) {
      setError(`A quantidade deve estar entre 1 e ${maxRegistros.toLocaleString('pt-BR')}`);
      return false;
    }
    return true;
  };

  const handleGenerateAndImport = async () => {
    setError(null);
    setSuccess(null);
    if (!isValid() || !selected) return;

    setPhase('generating');

    try {
      const genResponse = await api.post('/csv/generate', {
        generator: selected.generator,
        n_registros: nRegistros,
      }, {
        responseType: 'blob',
        timeout: 120000,
      });

      const disposition = genResponse.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `dataset_${selected.generator}.csv`;

      const targetName = genResponse.headers['x-target-name'] || null;
      const userStoryRaw = genResponse.headers['x-user-story'] || null;
      const userStory = userStoryRaw ? decodeURIComponent(userStoryRaw) : null;

      const blob = new Blob([genResponse.data], { type: 'text/csv' });
      const file = new File([blob], filename, { type: 'text/csv' });

      setPhase('importing');

      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await api.post('/csv/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 5 * 60 * 1000,
      });

      const uploadData = uploadResponse.data?.data;
      if (!uploadData?.tempPath || !uploadData?.columns) {
        throw new Error('Resposta do upload inválida');
      }

      const tableName = filename.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const columns = uploadData.columns.map((c: any) => c.name);

      const confirmResponse = await api.post('/csv/confirm', {
        tempPath: uploadData.tempPath,
        tableName,
        columns,
      }, {
        timeout: 10 * 60 * 1000,
      });

      if (!confirmResponse.data?.success) {
        throw new Error(confirmResponse.data?.message || 'Erro ao importar');
      }

      const totalInserido = confirmResponse.data.data?.recordCount ?? nRegistros;
      setSuccess(
        `✅ ${totalInserido} registros importados na tabela "${tableName}". Redirecionando...`
      );
      setPhase('done');

      setTimeout(() => {
        navigate('/orc', {
          state: {
            datasetName: tableName,
            targetName,
            userStory,
            autoStart: true,
          },
        });
      }, 1500);

    } catch (err: any) {
      setPhase('idle');
      if (err.response?.status === 400) {
        setError(err.response.data?.message || 'Dados inválidos.');
      } else if (err.response?.status === 401) {
        setError('Sessão expirada. Faça login novamente.');
      } else if (err.response?.status === 500) {
        setError(err.response.data?.message || 'Erro no servidor.');
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Tempo esgotado. Tente menos registros.');
      } else if (!err.response) {
        setError('Não foi possível contatar o servidor. Verifique sua conexão.');
      } else {
        setError(err.response?.data?.message || err.message || 'Erro ao gerar e importar.');
      }
    }
  };

  const handleDownloadOnly = async () => {
    setError(null);
    setSuccess(null);
    if (!isValid() || !selected) return;

    setDownloading(true);

    try {
      const response = await api.post('/csv/generate', {
        generator: selected.generator,
        n_registros: nRegistros,
      }, {
        responseType: 'blob',
        timeout: 120000,
      });

      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `dataset_${selected.generator}.csv`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`✅ CSV baixado: ${filename}`);

    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao baixar o CSV.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSelect = (category: Category) => {
    if (phase !== 'idle') return;
    setSelected(category);
    setError(null);
    setSuccess(null);
  };

  const busy = phase !== 'idle' && phase !== 'done';

  return (
    <div className="csv-generator-container">
      <div className="csv-generator-card">
        <h2>🧪 Gerar dados de teste</h2>
        <p className="subtitle">
          Escolha o tipo de problema que você quer resolver. A plataforma vai gerar um
          CSV com dados de exemplo e já importar para você experimentar o pipeline
          completo — do dado bruto até a predição.
        </p>

        <div className="categories-grid">
          {CATEGORIES.map((category) => {
            const isSelected = selected?.key === category.key;
            return (
              <button
                key={category.key}
                type="button"
                className={`category-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(category)}
                disabled={busy}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-label">{category.label}</span>
                <span className="category-scientific">({category.scientific})</span>
                <span className="category-description">{category.description}</span>
                <span className="category-examples">{category.examples}</span>
              </button>
            );
          })}
        </div>

        {selected && (
          <div className="generator-form">
            <div className="selected-summary">
              <span className="selected-icon">{selected.icon}</span>
              <div>
                <strong>{selected.label}</strong>
                <span className="selected-scientific">({selected.scientific})</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="n-registros">
                Quantas linhas de exemplo você quer gerar?
                <span className="hint">
                  (entre 1 e {maxRegistros.toLocaleString('pt-BR')})
                </span>
              </label>
              <input
                id="n-registros"
                type="number"
                min={1}
                max={maxRegistros}
                value={nRegistros}
                onChange={(e) => setNRegistros(parseInt(e.target.value, 10) || 0)}
                disabled={busy}
              />
              <span className="field-hint">
                Mais linhas = mais dados para o modelo aprender. 500 é um bom começo.
              </span>
            </div>

            <button
              type="button"
              className="btn-generate-import"
              onClick={handleGenerateAndImport}
              disabled={busy || nRegistros < 1 || nRegistros > maxRegistros}
            >
              {phase === 'generating' && '⏳ Gerando CSV...'}
              {phase === 'importing' && '⏳ Importando...'}
              {phase === 'done' && '✅ Importado! Redirecionando...'}
              {phase === 'idle' && '⚡ Gerar e importar'}
            </button>

            {phase === 'generating' && (
              <p className="progress-hint">
                Gerando o CSV com {nRegistros} linhas de exemplo...
              </p>
            )}
            {phase === 'importing' && (
              <p className="progress-hint">
                Importando os dados para o banco. Isso pode levar alguns segundos...
              </p>
            )}
            {phase === 'done' && (
              <p className="progress-hint">
                Tudo pronto! Redirecionando para o processamento...
              </p>
            )}

            <div className="secondary-action">
              <span className="secondary-label">
                Prefere só baixar o CSV para usar fora da plataforma?
              </span>
              <button
                type="button"
                className="btn-download-link"
                onClick={handleDownloadOnly}
                disabled={busy || downloading || nRegistros < 1 || nRegistros > maxRegistros}
              >
                {downloading ? '⏳ Baixando...' : 'Baixar apenas o CSV'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">❌ {error}</div>
        )}

        {success && (
          <div className="success-message">{success}</div>
        )}
      </div>
    </div>
  );
};

export default CsvGeneratorInner;